import React, { useState, useCallback, useEffect } from 'react';
import CommunityGrid from './CommunityGrid';
import CommunityDetailPanel from './CommunityDetailPanel';
import UnifiedSearchBar from './UnifiedSearchBar';
import DocumentDNAList from './DocumentDNAList';
import DocumentDNADetail from './DocumentDNADetail';
import DocumentNetworkGraph from './DocumentNetworkGraph';
import KnowledgeTimelineChart from './KnowledgeTimelineChart';
import EntityContextWindow from './EntityContextWindow';

/**
 * KnowledgeGraphPage
 * Top-level orchestrator for the Knowledge Graph section.
 *
 * View hierarchy:
 *   community-grid     → CommunityGrid (default)
 *   community-detail   → CommunityDetailPanel
 *   doc-dna            → DocumentDNAList
 *   doc-dna-detail     → DocumentDNADetail  (Full DNA View for a single doc)
 *   doc-network        → DocumentNetworkGraph
 *   doc-timeline       → KnowledgeTimelineChart
 *
 * Entity context is displayed as a modal overlay (EntityContextWindow)
 * regardless of the current sub-view.
 */

const SUB_TABS = [
  { id: 'community-grid', label: 'Communities', icon: 'fas fa-diagram-project' },
  { id: 'doc-dna',        label: 'Doc DNA',     icon: 'fas fa-dna'             },
  { id: 'doc-network',    label: 'Doc Network', icon: 'fas fa-circle-nodes'    },
  { id: 'doc-timeline',   label: 'Timeline',    icon: 'fas fa-chart-line'      },
];

const KnowledgeGraphPage = ({ initialEntityId, initialEntityName } = {}) => {
  const [subView, setSubView]               = useState('community-grid');
  const [selectedCommunity, setSelectedCommunity] = useState(null);
  const [selectedDNADoc, setSelectedDNADoc] = useState(null); // doc selected for Full DNA View

  // Entity context modal state
  const [contextEntity, setContextEntity]   = useState(null); // { id, entity_name, entity_type }

  // Deep-link: open entity context window when initialEntityId is provided
  useEffect(() => {
    if (initialEntityId) {
      setContextEntity({
        id: initialEntityId,
        entity_name: initialEntityName || '',
        entity_type: 'UNKNOWN',
      });
    }
  }, [initialEntityId, initialEntityName]);

  // Navigate to community detail
  const handleSelectCommunity = useCallback((community) => {
    setSelectedCommunity(community);
    setSubView('community-detail');
  }, []);

  // Navigate back to community grid
  const handleBackToCommunities = useCallback(() => {
    setSelectedCommunity(null);
    setSubView('community-grid');
  }, []);

  // Open the entity context modal
  const handleOpenEntityContext = useCallback((entity) => {
    const id = entity?.id || entity?.node_id;
    if (!id) return;
    setContextEntity({
      id,
      entity_name: entity.entity_name || entity.name || entity.label || '',
      entity_type: entity.entity_type || entity.type || 'UNKNOWN',
    });
  }, []);

  const handleSubTabClick = (id) => {
    setSubView(id);
    if (id === 'community-grid') setSelectedCommunity(null);
    // If the user clicks "Doc DNA" tab while in doc-dna-detail, go back to list
    if (id === 'doc-dna') setSelectedDNADoc(null);
  };

  return (
    <div className="kgp-container">
      {/* ── Global search bar ── */}
      <div className="kgp-search-row">
        <UnifiedSearchBar
          onSelectCommunity={handleSelectCommunity}
          onSelectEntity={handleOpenEntityContext}
          placeholder="Search topics, entities, or evidence…"
        />
      </div>

      {/* ── Sub-navigation tabs ── */}
      <div className="kgp-subtabs">
        {SUB_TABS.map(tab => (
          <button
            key={tab.id}
            className={`kgp-subtab ${(subView === tab.id || (subView === 'community-detail' && tab.id === 'community-grid')) ? 'active' : ''}`}
            onClick={() => handleSubTabClick(tab.id)}
          >
            <i className={tab.icon}></i>
            <span>{tab.label}</span>
          </button>
        ))}
      </div>

      {/* ── Main content area ── */}
      <div className="kgp-content">

        {/* Community Grid (default landing) */}
        {subView === 'community-grid' && (
          <CommunityGrid onSelectCommunity={handleSelectCommunity} />
        )}

        {/* Community Detail (drill-down) */}
        {subView === 'community-detail' && selectedCommunity && (
          <CommunityDetailPanel
            community={selectedCommunity}
            onBack={handleBackToCommunities}
            onOpenEntity={handleOpenEntityContext}
          />
        )}

        {/* Document DNA — list */}
        {subView === 'doc-dna' && !selectedDNADoc && (
          <DocumentDNAList
            onSelectDoc={(doc) => {
              setSelectedDNADoc(doc);
            }}
            onExplore={(community) => {
              if (community?.community_id) {
                handleSelectCommunity({ id: community.community_id, title: community.community_title });
              }
            }}
          />
        )}

        {/* Document DNA — Full DNA Detail view */}
        {subView === 'doc-dna' && selectedDNADoc && (
          <DocumentDNADetail
            doc={selectedDNADoc}
            onBack={() => setSelectedDNADoc(null)}
            onSelectCommunity={handleSelectCommunity}
          />
        )}

        {/* Document Network */}
        {subView === 'doc-network' && (
          <DocumentNetworkGraph
            onEntityClick={(entityName) => {
              // entity name only — not enough to open context window (needs UUID)
              // so this is a no-op for now; a future enhancement could resolve by name
              console.log('[KGP] Doc network entity click:', entityName);
            }}
          />
        )}

        {/* Knowledge Timeline */}
        {subView === 'doc-timeline' && (
          <KnowledgeTimelineChart
            onSelectDoc={(doc) => {
              console.log('[KGP] Timeline doc selected:', doc);
            }}
          />
        )}
      </div>

      {/* ── Entity Context Modal (global overlay) ── */}
      {contextEntity && (
        <EntityContextWindow
          entityId={contextEntity.id}
          entityName={contextEntity.entity_name}
          entityType={contextEntity.entity_type}
          onClose={() => setContextEntity(null)}
        />
      )}
    </div>
  );
};

export default KnowledgeGraphPage;
